import { routeHash, router } from "./router.js";

const pages = new Map();
/** Settings section a management page lives in (`new-bunny` → `agents`). */
const settingsSections = new Map([
  ["bunnies", "agents"],
  ["new-bunny", "agents"],
]);
/**
 * The settings section (Team or Personal) a page belongs to, if any. Settings keeps its
 * navigation and breadcrumb while such a page is open (ADM-02).
 * @param {string | undefined} name
 */
export function settingsSectionOf(name) {
  if (name === undefined) return undefined;
  // A page under a settings path (`#/settings/me/media/new`) belongs to that
  // section even before the module that builds it has registered it.
  return (
    settingsSections.get(name) ||
    /^#\/settings\/(?:team|me)\/([a-z]+)\//u.exec(
      routeHash({ name: "page", page: name }),
    )?.[1]
  );
}
/**
 * Move focus to a page's heading after a route change. The heading is not a
 * control, so no focus ring appears; the next Tab starts from the page top.
 * @param {ParentNode} page
 */
export function focusPageHeading(page) {
  const heading = page.querySelector(".ui-page-header__title");
  if (heading) heading.focus({ preventScroll: true });
  else page.querySelector("button, input, select")?.focus();
}
export function syncPageShell(app = document.querySelector(".app-shell")) {
  if (!app) return;
  const settings = document.querySelector(".settings-page:not([hidden])");
  const activePage =
    settings || document.querySelector(".management-page:not([hidden])");
  const active = Boolean(activePage);
  // The account menu lives in the persistent sidebar footer (ADR-022); pages no longer adopt it.
  app.hidden = false;
  app.inert = false;
  app.classList?.toggle?.("page-shell", active);
  app.classList?.toggle?.("settings-shell", Boolean(settings));
  const skip = document.querySelector(".skip-link");
  if (skip) {
    skip.href = settings ? "#settings-page" : "#main-content";
    skip.onclick = settings
      ? (event) => {
          event.preventDefault();
          settings.focus({ preventScroll: true });
        }
      : null;
  }
  const sidebar = document.querySelector("#sidebar");
  if (sidebar) {
    const mode = settings ? "settings" : "";
    sidebar.dataset.page = mode;
    sidebar.setAttribute(
      "aria-label",
      mode === "settings" ? "Management navigation" : "Main navigation",
    );
  }
  // The chat also stays out of reach while a phone overlay (the drawer or the
  // sidebar sheet) covers it; route changes must not undo that.
  const body = document.body;
  const covered = Boolean(
    body?.classList?.contains?.("dock-overlay") ||
      body?.classList?.contains?.("sidebar-open"),
  );
  const main = app.querySelector(".main-pane");
  if (main) main.inert = active || covered;
  const dock = app.querySelector(".workspace-dock");
  if (dock) dock.inert = active;
}
export function leaveManagement() {
  if (["page", "settings"].includes(router.current.name)) router.navigate("");
  else router.refresh();
}
/** Show the management page named by the route; every route change runs this first. */
export function syncManagementPages() {
  router.refresh();
}
router.subscribe(syncPages);
function syncPages(route = router.current) {
  const active = route.name === "page" ? pages.get(route.page) : undefined;
  const allowed = active?.allowed();
  let closed;
  for (const record of pages.values()) {
    const visible = record === active && allowed;
    const changed = record.page.hidden === visible;
    record.page.hidden = !visible;
    // A record page (`…/:id`, `…/:id/…/:item`) shows again when the record changes.
    const id =
      visible && route.name === "page" && (route.id || route.item)
        ? `${route.id || ""}/${route.item || ""}`
        : undefined;
    if (visible && !changed && id !== record.id) {
      record.id = id;
      record.onShow(route);
      focusPageHeading(record.page);
    }
    record.id = id;
    if (changed) {
      if (visible) {
        record.onShow(route);
        focusPageHeading(record.page);
      } else {
        closed = record;
        record.page.dispatchEvent(new Event("close"));
      }
    }
  }
  const app = document.querySelector(".app-shell");
  syncPageShell(app);
  if (closed && !allowed && route.name !== "settings") {
    const target =
      closed.returnFocus?.isConnected &&
      !closed.returnFocus.closest("[hidden]") &&
      !closed.returnFocus.disabled &&
      closed.returnFocus.getClientRects?.().length !== 0
        ? closed.returnFocus
        : app?.querySelector("#message-input");
    target?.focus({ preventScroll: true });
  }
}
/**
 * Register a page shown by the route `{ name: "page", page: name }`.
 * `section` names the settings section the page belongs to; it is then
 * mounted inside that section (or `parent`) and Settings keeps its chrome.
 */
export function createManagementPage({
  page,
  name,
  allowed = () => true,
  onShow = (_route) => {},
  section = undefined,
  parent = undefined,
}) {
  if (section) settingsSections.set(name, section);
  const host =
    parent ||
    document.querySelector(
      settingsSectionOf(name)
        ? `#preference-${settingsSectionOf(name)}`
        : ".app-shell",
    );
  host?.append(page);
  page.hidden = true;
  page.classList.add("management-page");
  let previous = "",
    returnFocus;
  const record = { page, allowed, onShow };
  pages.set(name, record);
  const here = () => {
    const route = router.current;
    return route.name === "page" && route.page === name;
  };
  const controller = {
    get visible() {
      return !page.hidden;
    },
    /**
     * @param {string} [tab] Section shown in the URL (`#/automations/monitors`).
     * @param {{ id?: string, item?: string }} [options] The record a `…/:id`
     *   page shows, and the record under it on a `…/:id/…/:item` page.
     */
    open(tab, { id, item } = {}) {
      if (!allowed()) return;
      if (page.hidden && !here()) {
        previous = location.hash;
        returnFocus = document.activeElement;
        record.returnFocus = returnFocus;
      }
      router.navigate({
        name: "page",
        page: name,
        ...(tab && { tab }),
        ...(id && { id }),
        ...(item && { item }),
      });
    },

    /** Record the visible tab in the URL without a new history entry. */
    setTab(tab) {
      if (here())
        router.navigate(
          { name: "page", page: name, tab },
          { replace: true, silent: true },
        );
    },
    close() {
      if (here()) {
        router.navigate(previous || "");
        if (
          returnFocus?.isConnected &&
          !returnFocus.closest("[hidden]") &&
          !returnFocus.disabled &&
          returnFocus.getClientRects?.().length !== 0
        )
          returnFocus.focus();
      }
    },
    reset() {
      previous = "";
      if (here()) router.navigate("", { replace: true });
      else router.refresh();
    },
  };
  page.addEventListener("keydown", (event) => {
    if (
      event.key !== "Escape" ||
      event.isComposing ||
      event.defaultPrevented ||
      document.querySelector(":popover-open")
    )
      return;
    event.preventDefault();
    event.stopPropagation();
    controller.close();
  });
  return controller;
}
